const BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';

let csrfToken = $state(null);

export function setCSRFToken(token) {
  csrfToken = token;
}
export function getCSRFToken() {
  return csrfToken;
}

export class ApiError extends Error {
  constructor(status, body) {
    super(`API ${status}: ${body?.error ?? 'unknown error'}`);
    this.status = status;
    this.body = body;
  }
}

async function request(method, path, body) {
  const opts = {
    method,
    credentials: 'include',
    headers: {},
  };
  if (body !== undefined) {
    opts.headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  }
  if (csrfToken) {
    opts.headers['X-CSRF-Token'] = csrfToken;
  }
  const res = await fetch(`${BASE}${path}`, opts);
  const text = await res.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    data = null;
  }
  if (!res.ok) throw new ApiError(res.status, data);
  return data;
}

export const api = {
  get: (path) => request('GET', path),
  post: (path, body) => request('POST', path, body),
  put: (path, body) => request('PUT', path, body),
  del: (path) => request('DELETE', path),

  login(password, code) {
    return api.post('/api/auth/login', code ? { password, code } : { password });
  },
  twoFactorStatus() {
    return api.get('/api/auth/2fa');
  },
  twoFactorSetup(password) {
    return api.post('/api/auth/2fa/setup', { password });
  },
  twoFactorEnable(code) {
    return api.post('/api/auth/2fa/enable', { code });
  },
  twoFactorDisable(password, code) {
    return api.post('/api/auth/2fa/disable', { password, code });
  },
  logout() {
    return api.post('/api/auth/logout');
  },
  verify() {
    return api.get('/api/auth/verify');
  },

  getProfile() {
    return api.get('/api/profile');
  },
  updateProfile(profile) {
    return api.put('/api/profile', profile);
  },

  getSettings() {
    return api.get('/api/profile/settings');
  },
  updateSettings(settings) {
    return api.put('/api/profile/settings', settings);
  },

  listProjects() {
    return api.get('/api/projects');
  },
  createProject(p) {
    return api.post('/api/projects', p);
  },
  updateProject(id, p) {
    return api.put(`/api/projects/${id}`, p);
  },
  deleteProject(id) {
    return api.del(`/api/projects/${id}`);
  },

  submitContact(data) {
    return api.post('/api/contact', data);
  },
  listContactMessages() {
    return api.get('/api/contact');
  },

  deleteChatMessage(id) {
    return api.del(`/api/chat/messages/${id}`);
  },
  listBans() {
    return api.get('/api/chat/bans');
  },
  banUser(nick, reason) {
    return api.post('/api/chat/bans', { nick, reason });
  },
  unbanUser(nick) {
    return api.del(`/api/chat/bans/${nick}`);
  },

  fsList() {
    return api.get('/api/fs');
  },
  fsCreate(payload) {
    return api.post('/api/fs/node', payload);
  },
  fsUpdate(id, changes) {
    return api.put(`/api/fs/node/${id}`, changes);
  },
  fsDelete(id) {
    return api.del(`/api/fs/node/${id}`);
  },
  fsNuke() {
    return api.del('/api/fs/all');
  },
  fsURL(id) {
    return api.get(`/api/fs/node/${id}/url`);
  },

  ioc: {
    overview: () => api.get('/api/ioc/overview'),
    listServers: () => api.get('/api/ioc/servers'),
    createServer: (payload) => api.post('/api/ioc/servers', payload),
    getServer: (id) => api.get(`/api/ioc/servers/${id}`),
    updateServer: (id, changes) => api.put(`/api/ioc/servers/${id}`, changes),
    deleteServer: (id) => api.del(`/api/ioc/servers/${id}`),
    setMaintenance: (id, on) => api.put(`/api/ioc/servers/${id}/maintenance`, { maintenance: on }),
    serverMetrics: (id) => api.get(`/api/ioc/servers/${id}/metrics`),
    serverContainers: (id) => api.get(`/api/ioc/servers/${id}/containers`),
    serverDeployments: (id) => api.get(`/api/ioc/servers/${id}/deployments`),

    listAlerts: () => api.get('/api/ioc/alerts'),
    serverAlerts: (id) => api.get(`/api/ioc/servers/${id}/alerts`),
    listAlertRules: () => api.get('/api/ioc/alert-rules'),
    createAlertRule: (rule) => api.post('/api/ioc/alert-rules', rule),
    updateAlertRule: (id, rule) => api.put(`/api/ioc/alert-rules/${id}`, rule),
    deleteAlertRule: (id) => api.del(`/api/ioc/alert-rules/${id}`),
    resolveAlert: (id) => api.post(`/api/ioc/alert-instances/${id}/resolve`),
    muteAlert: (id) => api.post(`/api/ioc/alert-instances/${id}/mute`),
    assignAlert: (id, payload) => api.put(`/api/ioc/alert-instances/${id}`, payload),

    listChannels: () => api.get('/api/ioc/notification-channels'),
    createChannel: (payload) => api.post('/api/ioc/notification-channels', payload),
    deleteChannel: (id) => api.del(`/api/ioc/notification-channels/${id}`),

    // Observability (Phase 3).
    listEvents: (params) => api.get(`/api/ioc/events${iocQuery(params)}`),
    serverEvents: (id, params) => api.get(`/api/ioc/servers/${id}/events${iocQuery(params)}`),
    searchLogs: (params) => api.get(`/api/ioc/logs${iocQuery(params)}`),
    listLogBookmarks: () => api.get('/api/ioc/log-bookmarks'),
    createLogBookmark: (payload) => api.post('/api/ioc/log-bookmarks', payload),
    deleteLogBookmark: (id) => api.del(`/api/ioc/log-bookmarks/${id}`),
    metricsHistory: (id, range) =>
      api.get(`/api/ioc/servers/${id}/metrics/history?range=${encodeURIComponent(range)}`),
    snapshotDiff: (id, from, to) =>
      api.get(`/api/ioc/servers/${id}/snapshot-diff?from=${from}&to=${to}`),
    gitopsDeployments: (id) => api.get(`/api/ioc/servers/${id}/gitops-deployments`),

    // Actions & automation (Phase 4). Every operator action is admin-gated and
    // audited server-side; the container ops are the SAFE allowlisted subset.
    listActions: (params) => api.get(`/api/ioc/actions${iocQuery(params)}`),
    cancelAction: (id) => api.del(`/api/ioc/actions/${id}`),
    restartContainer: (id, cid) => api.post(`/api/ioc/servers/${id}/containers/${cid}/restart`),
    stopContainer: (id, cid) => api.post(`/api/ioc/servers/${id}/containers/${cid}/stop`),
    startContainer: (id, cid) => api.post(`/api/ioc/servers/${id}/containers/${cid}/start`),
    listAudit: (params) => api.get(`/api/ioc/audit${iocQuery(params)}`),

    listMaintenanceWindows: () => api.get('/api/ioc/maintenance-windows'),
    serverMaintenanceWindows: (id) => api.get(`/api/ioc/servers/${id}/maintenance-windows`),
    createMaintenanceWindow: (payload) => api.post('/api/ioc/maintenance-windows', payload),
    deleteMaintenanceWindow: (id) => api.del(`/api/ioc/maintenance-windows/${id}`),

    listBackups: () => api.get('/api/ioc/backups'),
    serverBackups: (id) => api.get(`/api/ioc/servers/${id}/backups`),

    listHealingPolicies: () => api.get('/api/ioc/healing-policies'),
    createHealingPolicy: (payload) => api.post('/api/ioc/healing-policies', payload),
    updateHealingPolicy: (id, payload) => api.put(`/api/ioc/healing-policies/${id}`, payload),
    deleteHealingPolicy: (id) => api.del(`/api/ioc/healing-policies/${id}`),

    // Insight (Phase 5) — global search, anomalies, capacity, cost-lite.
    search: (q) => api.get(`/api/ioc/search${iocQuery({ q })}`),
    serverAnomalies: (id) => api.get(`/api/ioc/servers/${id}/anomalies`),
    serverCapacity: (id) => api.get(`/api/ioc/servers/${id}/capacity`),
    serverCost: (id) => api.get(`/api/ioc/servers/${id}/cost`),
    putServerCost: (id, payload) => api.put(`/api/ioc/servers/${id}/cost`, payload),
    costOverview: () => api.get('/api/ioc/cost/overview'),

    // Guest Radar (ADR 004) — admin-only reads. Aggregate/anonymous data only.
    radarVisits: (params) => api.get(`/api/ioc/radar/visits${iocQuery(params)}`),
    radarVisit: (id) => api.get(`/api/ioc/radar/visits/${id}`),
    radarLive: (since) => api.get(`/api/ioc/radar/live${iocQuery({ since })}`),
    radarStats: () => api.get('/api/ioc/radar/stats'),
    deleteVisit: (id) => api.del(`/api/ioc/radar/visits/${id}`),
  },
};

// Guest Radar PUBLIC collection endpoints. These are unauthenticated: they must
// NOT carry admin cookies or the CSRF token, so they use a bare fetch against
// BASE with credentials omitted. All calls fail silently — analytics must never
// break the OS.
export const radar = {
  async visit(payload) {
    return radarPost('/api/ioc/radar/visit', payload, true);
  },
  async event(payload) {
    return radarPost('/api/ioc/radar/event', payload, false);
  },
  async heartbeat(payload) {
    return radarPost('/api/ioc/radar/heartbeat', payload, false);
  },
  async count() {
    try {
      const res = await fetch(`${BASE}/api/ioc/radar/count`, { credentials: 'omit' });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  },
};

async function radarPost(path, payload, wantJSON) {
  try {
    const res = await fetch(`${BASE}${path}`, {
      method: 'POST',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload ?? {}),
    });
    if (!res.ok) return null;
    return wantJSON ? await res.json() : null;
  } catch {
    return null;
  }
}

// iocQuery builds a query string from a params object, skipping empty values.
// Uses manual encoding rather than URLSearchParams to satisfy the reactive-file
// lint rule (no mutable built-ins in .svelte.js).
function iocQuery(params) {
  if (!params) return '';
  const parts = [];
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

// openIOCStream consumes the IOC SSE feed via fetch (EventSource cannot send the
// X-CSRF-Token header the admin middleware requires). Resolves when the stream
// ends; abort via the provided AbortSignal. onMessage receives each parsed
// { type, data } envelope.
export async function openIOCStream({ onMessage, signal }) {
  const headers = {};
  if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
  const res = await fetch(`${BASE}/api/ioc/stream`, { credentials: 'include', headers, signal });
  if (!res.ok || !res.body) throw new ApiError(res.status, null);

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep;
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      parseSSEFrame(buffer.slice(0, sep), onMessage);
      buffer = buffer.slice(sep + 2);
    }
  }
}

function parseSSEFrame(frame, onMessage) {
  const dataLines = [];
  for (const line of frame.split('\n')) {
    if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
  }
  if (dataLines.length === 0) return;
  try {
    onMessage(JSON.parse(dataLines.join('\n')));
  } catch {
    // ignore malformed frame
  }
}
